import { useState, useEffect, useCallback } from 'react';
import { getTeamAvatar, getTeamColor } from './teamAvatars';

const STORAGE_KEY = 'biblika_team_mode';

export function useTeamMode() {
  const [isTeamMode, setIsTeamMode] = useState(false);
  const [teams, setTeams] = useState([]);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const [teamScores, setTeamScores] = useState({});
  const [gameStarted, setGameStarted] = useState(false);

  // Load team state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setIsTeamMode(data.isTeamMode || false);
        setTeams(data.teams || []);
        setCurrentTeamIndex(data.currentTeamIndex || 0);
        setTeamScores(data.teamScores || {});
        setGameStarted(data.gameStarted || false);
      } catch (e) {
        console.warn('Failed to load team mode data:', e);
      }
    }
  }, []);

  // Save team state to localStorage
  useEffect(() => {
    if (isTeamMode || teams.length > 0) {
      const data = {
        isTeamMode,
        teams,
        currentTeamIndex,
        teamScores,
        gameStarted
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  }, [isTeamMode, teams, currentTeamIndex, teamScores, gameStarted]);

  const setupTeams = useCallback((teamNames) => {
    const newTeams = teamNames.map((name, index) => ({
      id: `team_${index}`,
      name: name || `Team ${index + 1}`,
      avatar: getTeamAvatar(index),
      color: getTeamColor(index)
    }));
    
    const newScores = {};
    newTeams.forEach(team => {
      newScores[team.id] = 0;
    });

    setTeams(newTeams);
    setTeamScores(newScores);
    setCurrentTeamIndex(0);
    setIsTeamMode(true);
    setGameStarted(false);
  }, []);

  const startTeamGame = useCallback(() => {
    setGameStarted(true);
  }, []);

  const addPoints = useCallback((points = 1) => {
    if (!teams.length) return;
    
    const currentTeam = teams[currentTeamIndex];
    setTeamScores(prev => ({
      ...prev,
      [currentTeam.id]: (prev[currentTeam.id] || 0) + points
    }));
  }, [teams, currentTeamIndex]);

  const nextTeam = useCallback(() => {
    if (!teams.length) return;
    setCurrentTeamIndex(prev => (prev + 1) % teams.length);
  }, [teams.length]);

  const resetGame = useCallback(() => {
    const resetScores = {};
    teams.forEach(team => {
      resetScores[team.id] = 0;
    });
    setTeamScores(resetScores);
    setCurrentTeamIndex(0);
    setGameStarted(false);
  }, [teams]);

  const exitTeamMode = useCallback(() => {
    setIsTeamMode(false);
    setTeams([]);
    setTeamScores({});
    setCurrentTeamIndex(0);
    setGameStarted(false);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getCurrentTeam = useCallback(() => {
    return teams[currentTeamIndex] || null;
  }, [teams, currentTeamIndex]);

  const getLeaderboard = useCallback(() => {
    return teams
      .map(team => ({
        ...team,
        score: teamScores[team.id] || 0
      }))
      .sort((a, b) => b.score - a.score);
  }, [teams, teamScores]);

  return {
    // State
    isTeamMode,
    teams,
    currentTeamIndex,
    teamScores,
    gameStarted,
    
    // Actions
    setupTeams,
    startTeamGame,
    addPoints,
    nextTeam,
    resetGame,
    exitTeamMode,
    
    // Computed
    getCurrentTeam,
    getLeaderboard,
    
    // Utils
    currentTeam: getCurrentTeam(),
    leaderboard: getLeaderboard(),
    hasTeams: teams.length > 0
  };
}